import {
  Badge,
  Caption1,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuItemCheckbox,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tab,
  TabList,
  TableCellLayout,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  Cube16Regular,
  CalendarLtr16Regular,
  ChevronDown12Regular,
  DataPie16Regular,
  LockClosed16Regular,
  Money16Regular,
  Options16Regular,
  TableSimple16Regular,
} from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/app-dialog";
import { FuiButton, FuiFilterSelect } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListFilterMenu, ListSearch, ListToolbar } from "@/components/list-toolbar";
import { contentShell, pageMax, pageStackAt, shellGutter } from "@/shell/content-frame";
import {
  PhaseProgressVisual,
  StackedBar,
  StackedColumnsVisual,
  TrendLineVisual,
  niceTicks,
  type LineSeries,
  type PhaseProgressDatum,
  type Segment,
  type StackedColumnDatum,
} from "@/components/visual/charts";
import { SERIES, toneColor } from "@/components/visual/chart-colors";
import { ReportVisual, type VisualTable } from "@/components/visual/ReportVisual";
import { ShareBoard } from "@/components/visual/ink-board";
import type { SolutionCategory, SolutionItem } from "@/engine/types";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { useCan, useMoneyMasked } from "@/features/access/use-plan-access";
import { BudgetEditDialog, CostOptionsDialog, SolutionFormDialog } from "@/features/resources/BudgetDialogs";
import {
  MM_MONTH_COL_WIDTH,
  MM_QUARTER_COL_WIDTH,
  mmMonthCellStyle,
  usePortfolioStyles,
  type TimeScale,
} from "@/features/resources/PortfolioViews";
import { PersonRowTrigger, RowMoreMenu } from "@/features/resources/RowMoreMenu";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import {
  distinctQuarters,
  distinctYears,
  fmtMillion,
  projectCostMonths,
  projectCostSummary,
  projectMonthlyBuckets,
  quarterOf,
  quarterStartMonth,
  type MonthBucket,
} from "@/features/resources/portfolio";
import {
  BILLING_CYCLE_LABEL,
  SOLUTION_CATEGORY_COLOR,
  SOLUTION_CATEGORY_LABEL,
  SOLUTION_CATEGORY_OPTIONS,
  SOLUTION_STATUS_LABEL,
} from "@/features/resources/resource-meta";
import { formatDate, formatDateRange } from "@/lib/date-format";
import { fmtCurrency, fmtMM } from "@/lib/format";
import { useCatalogStore } from "@/store/catalog-store";
import { ActualCostPanel } from "@/features/budget/ActualCostPanel";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `0 ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    height: "44px",
    minHeight: "44px",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  titles: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  nowrapBtn: {
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  quiet: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    containerType: "inline-size",
    containerName: "page",
  },
  pair: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: tokens.spacingHorizontalM,
    alignItems: "stretch",
    [pageMax(pageStackAt)]: {
      gridTemplateColumns: "1fr",
    },
  },
  visualStack: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  subBlock: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  subTitle: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  rowClick: {
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":hover td": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  rowSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    "& td": {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  subtotalRow: {
    backgroundColor: tokens.colorNeutralBackground2,
    "& td": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  nameCol: {
    minWidth: "240px",
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  nameHead: {
    zIndex: 3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  nameTotal: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  nameLink: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
    padding: 0,
    border: "none",
    background: "none",
    font: "inherit",
    color: "inherit",
    cursor: "pointer",
    textAlign: "left",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": { color: tokens.colorNeutralForeground1Hover },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  colFill: {
    width: "100%",
    minWidth: "220px",
  },
  colFixed: {
    width: "1%",
    whiteSpace: "nowrap",
  },
  dateCell: {
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  rowLabel: {
    width: "148px",
    minWidth: "148px",
    maxWidth: "148px",
    whiteSpace: "nowrap",
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  rowLabelHead: {
    zIndex: 3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  rowLabelTotal: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  summaryCol: {
    width: "120px",
    minWidth: "120px",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  shareCol: {
    width: "68px",
    minWidth: "68px",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  swatchRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  swatchDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  personCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    whiteSpace: "nowrap",
  },
  toneDanger: {
    color: tokens.colorPaletteRedForeground1,
  },
  toneSuccess: {
    color: tokens.colorPaletteGreenForeground1,
  },
  toneWarning: {
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  toneMuted: {
    color: tokens.colorNeutralForeground3,
  },
  colHighlight: {
    boxShadow: `inset 0 0 0 1.5px ${tokens.colorBrandStroke1}`,
  },
});

type View = "solutions" | "breakdown" | "timeline" | "actual";
const VIEWS: View[] = ["solutions", "breakdown", "timeline", "actual"];

type BudgetColKey =
  | "name"
  | "category"
  | "vendor"
  | "billing"
  | "period"
  | "unitCost"
  | "quantity"
  | "totalCost"
  | "share"
  | "assignee"
  | "status";

const BUDGET_COLUMNS: { key: BudgetColKey; label: string; defaultVisible: boolean; numeric?: boolean }[] = [
  { key: "name", label: "솔루션 / 품목", defaultVisible: true },
  { key: "category", label: "분류", defaultVisible: false },
  { key: "vendor", label: "벤더", defaultVisible: false },
  { key: "billing", label: "과금", defaultVisible: true },
  { key: "period", label: "계약 기간", defaultVisible: true, numeric: true },
  { key: "unitCost", label: "단가", defaultVisible: false, numeric: true },
  { key: "quantity", label: "수량", defaultVisible: true, numeric: true },
  { key: "totalCost", label: "총 비용", defaultVisible: true, numeric: true },
  { key: "share", label: "비중", defaultVisible: false, numeric: true },
  { key: "assignee", label: "전담", defaultVisible: true },
  { key: "status", label: "상태", defaultVisible: true },
];

const DEFAULT_VISIBLE_BUDGET_COLUMNS: BudgetColKey[] = BUDGET_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);

const BUDGET_COLUMNS_STORAGE_KEY = "wit:budget-solution-columns:v3";

function loadBudgetColumns(): BudgetColKey[] {
  try {
    const raw = localStorage.getItem(BUDGET_COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE_BUDGET_COLUMNS;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const valid = new Set(BUDGET_COLUMNS.map((c) => c.key));
      const filtered = parsed.filter((k): k is BudgetColKey => valid.has(k));
      if (filtered.length === 0) return DEFAULT_VISIBLE_BUDGET_COLUMNS;
      return filtered.includes("name") ? filtered : (["name", ...filtered] as BudgetColKey[]);
    }
  } catch {
    // fallback
  }
  return DEFAULT_VISIBLE_BUDGET_COLUMNS;
}

function saveBudgetColumns(cols: BudgetColKey[]) {
  try {
    localStorage.setItem(BUDGET_COLUMNS_STORAGE_KEY, JSON.stringify(cols));
  } catch {
    // ignore
  }
}

type SolutionStatusFilter = "all" | SolutionItem["status"];
type SolutionCategoryFilter = "all" | SolutionCategory;

const SOLUTION_STATUS_FILTER_OPTIONS: { value: SolutionStatusFilter; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "active", label: SOLUTION_STATUS_LABEL.active },
  { value: "contracted", label: SOLUTION_STATUS_LABEL.contracted },
  { value: "planned", label: SOLUTION_STATUS_LABEL.planned },
  { value: "terminated", label: SOLUTION_STATUS_LABEL.terminated },
];

const SOLUTION_CATEGORY_FILTER_OPTIONS: { value: SolutionCategoryFilter; label: string }[] = [
  { value: "all", label: "전체" },
  ...SOLUTION_CATEGORY_OPTIONS.map((option) => ({ value: option.value as SolutionCategory, label: option.label })),
];

function formatSolutionPeriod(sol: SolutionItem): string {
  if (sol.billingCycle === "once") {
    return sol.startDate ? formatDate(sol.startDate, "numeric") : "—";
  }
  return formatDateRange(sol.startDate, sol.endDate, "yearMonthShort");
}

type CostSegmentKey = "labor" | "overhead" | "fee" | "solution" | "expense";

export function ProjectBudgetPage() {
  const styles = useStyles();
  const pStyles = usePortfolioStyles();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const project = useProjectStore((s) => s.project);
  const derived = useProjectStore((s) => s.derived);
  const addSolution = useProjectStore((s) => s.addSolution);
  const updateSolution = useProjectStore((s) => s.updateSolution);
  const removeSolution = useProjectStore((s) => s.removeSolution);
  const updateBudget = useProjectStore((s) => s.updateBudget);
  const updateCostOptions = useProjectStore((s) => s.updateCostOptions);
  const { masked: isMasked } = useMoneyMasked();
  const canEditCost = useCan("plan.cost.edit");

  const resources = useCatalogStore((s) => s.resources);

  const rawView = searchParams.get("view");
  const view: View = VIEWS.includes(rawView as View) ? (rawView as View) : "solutions";
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<SolutionCategoryFilter>("all");
  const [statusFilter, setStatusFilter] = useState<SolutionStatusFilter>("all");
  const [selectedCostKey, setSelectedCostKey] = useState<CostSegmentKey | null>(null);
  const [breakdownVisualMode, setBreakdownVisualMode] = useState<"cards" | "ink-donut" | "ink-bar">("ink-donut");
  const [timeScale, setTimeScale] = useState<TimeScale>("month");
  const [yearFilter, setYearFilter] = useState("all");
  const [highlightedPeriodIdx, setHighlightedPeriodIdx] = useState<number | null>(null);

  const setView = (next: View) => {
    setQuery("");
    setSearchParams((prev) => {
      const current = prev.get("view");
      const currentView: View = VIEWS.includes(current as View) ? (current as View) : "solutions";
      if (currentView === next) return prev;
      const p = new URLSearchParams(prev);
      if (next === "solutions") p.delete("view");
      else p.set("view", next);
      return p;
    }, { replace: true });
  };

  const openSolutions = (category: SolutionCategoryFilter = "all") => {
    setStatusFilter("all");
    setCategoryFilter(category);
    setView("solutions");
  };

  const [visibleColumns, setVisibleColumns] = useState<BudgetColKey[]>(loadBudgetColumns);
  const [solutionDialog, setSolutionDialog] = useState<{ open: boolean; initial: SolutionItem | null }>({ open: false, initial: null });
  const [deleteTarget, setDeleteTarget] = useState<SolutionItem | null>(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);

  const handleColumnsChange = (cols: BudgetColKey[]) => {
    const next = cols.includes("name") ? cols : (["name", ...cols] as BudgetColKey[]);
    setVisibleColumns(next);
    saveBudgetColumns(next);
  };

  const visibleDefs = useMemo(
    () => BUDGET_COLUMNS.filter((c) => visibleColumns.includes(c.key)),
    [visibleColumns],
  );
  const colCount = Math.max(visibleDefs.length, 1);
  const totalIdx = visibleDefs.findIndex((c) => c.key === "totalCost");
  const colClass = (col: (typeof BUDGET_COLUMNS)[number], first: boolean, head = false) =>
    mergeClasses(
      head ? pStyles.th : pStyles.td,
      col.numeric && pStyles.num,
      !head && col.key === "period" && styles.dateCell,
      first || col.key === "name" ? mergeClasses(styles.colFill, styles.nameCol, head && styles.nameHead) : styles.colFixed,
    );

  const budget = project.budget;
  const solutions = useMemo(() => project.solutions ?? [], [project.solutions]);
  const filteredSolutions = useMemo(
    () =>
      solutions.filter((sol) => {
        if (categoryFilter !== "all" && sol.category !== categoryFilter) return false;
        if (statusFilter !== "all" && sol.status !== statusFilter) return false;
        const needle = query.trim().toLowerCase();
        if (!needle) return true;
        return `${sol.name} ${sol.vendor} ${sol.notes ?? ""}`.toLowerCase().includes(needle);
      }),
    [solutions, categoryFilter, statusFilter, query],
  );
  const filteredSolutionSum = useMemo(
    () => filteredSolutions.reduce((sum, s) => sum + (s.totalCost || 0), 0),
    [filteredSolutions],
  );

  const cost = useMemo(() => projectCostSummary(project, resources), [project, resources]);
  const totalLaborGroup = cost.directLabor + cost.overhead + cost.technicalFee;
  const burnPct = Math.round(cost.burn * 100);
  const solutionBurnPct = budget.targetSolutionCost > 0 ? Math.round((cost.solution / budget.targetSolutionCost) * 100) : 0;
  const solutionDiff = budget.targetSolutionCost - cost.solution;

  const allMonths = useMemo(() => projectCostMonths(project, derived.schedule.projectFinish), [project, derived.schedule.projectFinish]);
  const years = useMemo(() => distinctYears(allMonths), [allMonths]);
  const activeYear = years.includes(yearFilter) ? yearFilter : "all";
  const months = useMemo(
    () => (activeYear === "all" ? allMonths : allMonths.filter((month) => month.startsWith(activeYear))),
    [allMonths, activeYear],
  );

  const buckets = useMemo(() => projectMonthlyBuckets(project, resources, months), [project, resources, months]);

  const quarterBuckets = useMemo<MonthBucket[]>(() => {
    const qKeys = distinctQuarters(months);
    const map = new Map<string, MonthBucket>();
    for (const q of qKeys) {
      map.set(q, { ym: q, mm: 0, labor: 0, overhead: 0, technicalFee: 0, indirect: 0, solution: 0, expense: 0, total: 0 });
    }
    for (const b of buckets) {
      const q = quarterOf(b.ym);
      const acc = map.get(q);
      if (!acc) continue;
      acc.mm += b.mm;
      acc.labor += b.labor;
      acc.overhead += b.overhead;
      acc.technicalFee += b.technicalFee;
      acc.indirect += b.indirect;
      acc.solution += b.solution;
      acc.expense += b.expense;
      acc.total += b.total;
    }
    return qKeys.map((q) => map.get(q)!);
  }, [buckets, months]);

  const activeTimelineBuckets = timeScale === "quarter" ? quarterBuckets : buckets;
  const formatBucketLabel = (ymOrQ: string) =>
    timeScale === "quarter" ? formatDate(quarterStartMonth(ymOrQ), "quarterShort") : formatDate(ymOrQ, "yearMonthShort");
  const formatBucketFullLabel = (ymOrQ: string) =>
    timeScale === "quarter" ? formatDate(quarterStartMonth(ymOrQ), "quarter") : formatDate(ymOrQ, "yearMonth");

  const segments: Array<Segment<CostSegmentKey> & { hint: string }> = useMemo(
    () => [
      {
        key: "labor",
        label: "직접인건비",
        value: cost.directLabor,
        valueLabel: fmtCurrency(cost.directLabor, isMasked),
        color: SERIES.actual,
        hint: `배정 ${cost.headcount}명 · ${fmtMM(cost.plannedMM)} × 월 단가`,
      },
      {
        key: "overhead",
        label: "제경비",
        value: cost.overhead,
        valueLabel: fmtCurrency(cost.overhead, isMasked),
        color: SERIES.info,
        hint: budget.costOptions.includeOverhead ? `직접인건비의 ${Math.round((budget.costOptions.overheadRatio - 1) * 100)}%` : "산정 옵션 미적용",
      },
      {
        key: "fee",
        label: "기술료",
        value: cost.technicalFee,
        valueLabel: fmtCurrency(cost.technicalFee, isMasked),
        color: SERIES.good,
        hint: budget.costOptions.includeTechnicalFee ? `(직접인건비 + 제경비)의 ${Math.round(budget.costOptions.technicalFeeRatio * 100)}%` : "산정 옵션 미적용",
      },
      {
        key: "solution",
        label: "솔루션·라이선스",
        value: cost.solution,
        valueLabel: fmtCurrency(cost.solution, isMasked),
        color: SERIES.warn,
        hint: `${solutions.length}개 품목 · 목표 ${fmtCurrency(budget.targetSolutionCost, isMasked)}`,
      },
      {
        key: "expense",
        label: "직접경비",
        value: cost.expense,
        valueLabel: fmtCurrency(cost.expense, isMasked),
        color: SERIES.plan,
        hint: "출장·교육·기타 직접경비 편성액",
      },
    ],
    [cost, budget, solutions.length, isMasked],
  );
  const laborParts = segments.filter((part) => (part.key === "labor" || part.key === "overhead" || part.key === "fee") && part.value > 0);

  const solutionCategorySegments = useMemo<Segment<SolutionCategory>[]>(() => {
    const map = new Map<SolutionCategory, number>();
    for (const s of solutions) {
      map.set(s.category, (map.get(s.category) ?? 0) + (s.totalCost || 0));
    }
    const palette: Record<SolutionCategory, string> = {
      software: SERIES.actual,
      cloud: SERIES.info,
      saas: SERIES.good,
      license: SERIES.warn,
      hardware: SERIES.plan,
    };
    return (Object.keys(SOLUTION_CATEGORY_LABEL) as SolutionCategory[])
      .map((cat) => {
        const val = map.get(cat) ?? 0;
        return {
          key: cat,
          label: SOLUTION_CATEGORY_LABEL[cat],
          value: val,
          valueLabel: fmtCurrency(val, isMasked),
          color: palette[cat],
        };
      })
      .filter((seg) => seg.value > 0);
  }, [solutions, isMasked]);

  const budgetCompareBars = useMemo<PhaseProgressDatum[]>(() => {
    const items = [
      {
        key: "laborGroup",
        code: "1",
        label: "인건비 총계",
        projected: totalLaborGroup,
        target: budget.targetLaborCost,
        onClick: () => setSelectedCostKey((prev) => (prev === "labor" ? null : "labor")),
      },
      {
        key: "solutionGroup",
        code: "2",
        label: "솔루션·라이선스",
        projected: cost.solution,
        target: budget.targetSolutionCost,
        onClick: () => openSolutions(),
      },
      {
        key: "expenseGroup",
        code: "3",
        label: "직접경비",
        projected: cost.expense,
        target: budget.targetExpenseCost,
        onClick: () => setSelectedCostKey((prev) => (prev === "expense" ? null : "expense")),
      },
      {
        key: "totalBudget",
        code: "합계",
        label: "승인 예산 대비",
        projected: cost.total,
        target: cost.budget,
        onClick: canEditCost ? () => setBudgetOpen(true) : undefined,
      },
    ];

    return items.map((item) => {
      const denom = Math.max(item.projected, item.target, 1);
      const actualCoord = (item.projected / denom) * 100;
      const plannedCoord = item.target > 0 ? (item.target / denom) * 100 : null;
      const ratioPct = item.target > 0 ? Math.round((item.projected / item.target) * 100) : 0;
      const isOver = item.target > 0 && item.projected > item.target;
      const isWarn = !isOver && ratioPct >= 95;
      const tone = isOver ? "bad" : isWarn ? "warn" : "actual";
      return {
        key: item.key,
        wbs: item.code,
        label: item.label,
        actual: actualCoord,
        planned: plannedCoord,
        color: toneColor(tone),
        actualLabel: `${ratioPct}%`,
        plannedLabel: `/ 목표 ${fmtMillion(item.target, isMasked)}`,
        titleText: `${item.label} · 예상 ${fmtCurrency(item.projected, isMasked)} / 목표 ${fmtCurrency(item.target, isMasked)} (${ratioPct}%)`,
        onClick: item.onClick,
      };
    });
  }, [totalLaborGroup, budget, cost, isMasked, canEditCost]);

  const breakdownVisualTable = useMemo<VisualTable>(
    () => ({
      ariaLabel: "비용 구조 및 예산 대사 표",
      columns: [
        { key: "label", label: "계정 과목" },
        { key: "basis", label: "산정 기준" },
        { key: "projected", label: "예상 소진", numeric: true },
        { key: "share", label: "구성비", numeric: true },
      ],
      rows: segments.map((s) => ({
        key: s.key,
        cells: [
          s.label,
          s.hint,
          fmtCurrency(s.value, isMasked),
          `${cost.total ? Math.round((s.value / cost.total) * 100) : 0}%`,
        ],
      })),
    }),
    [segments, cost.total, isMasked],
  );

  const timelineStacks = useMemo<StackedColumnDatum[]>(
    () =>
      activeTimelineBuckets.map((b, idx) => {
        const label = formatBucketLabel(b.ym);
        const calloutTitle = formatBucketFullLabel(b.ym);
        const toM = (v: number) => Math.round((v / 1_000_000) * 10) / 10;
        return {
          key: b.ym,
          label,
          calloutTitle,
          onClick: () => setHighlightedPeriodIdx((prev) => (prev === idx ? null : idx)),
          parts: [
            {
              key: "labor",
              legend: "직접인건비",
              value: toM(b.labor),
              color: SERIES.actual,
              valueLabel: fmtCurrency(Math.round(b.labor), isMasked),
            },
            ...(b.indirect > 0
              ? [
                  {
                    key: "indirect",
                    legend: "제경비·기술료",
                    value: toM(b.indirect),
                    color: SERIES.info,
                    valueLabel: fmtCurrency(Math.round(b.indirect), isMasked),
                  },
                ]
              : []),
            {
              key: "solution",
              legend: "솔루션·라이선스",
              value: toM(b.solution),
              color: SERIES.warn,
              valueLabel: fmtCurrency(Math.round(b.solution), isMasked),
            },
            ...(b.expense > 0
              ? [
                  {
                    key: "expense",
                    legend: "직접경비",
                    value: toM(b.expense),
                    color: SERIES.plan,
                    valueLabel: fmtCurrency(Math.round(b.expense), isMasked),
                  },
                ]
              : []),
          ],
        };
      }),
    [activeTimelineBuckets, timeScale, isMasked],
  );

  const timelinePeakM = useMemo(() => {
    const maxVal = Math.max(1, ...activeTimelineBuckets.map((b) => b.total / 1_000_000));
    return niceTicks(Math.ceil(maxVal), 4);
  }, [activeTimelineBuckets]);

  const peakBucket = useMemo(() => {
    let best: MonthBucket | null = null;
    for (const b of activeTimelineBuckets) {
      if (!best || b.total > best.total) best = b;
    }
    return best;
  }, [activeTimelineBuckets]);

  const cumulativeModel = useMemo(() => {
    const labels = activeTimelineBuckets.map((b) => formatBucketLabel(b.ym));
    const calloutTitles = activeTimelineBuckets.map((b) => formatBucketFullLabel(b.ym));
    let running = 0;
    const cumValuesM: number[] = [];
    const cumLabels: string[] = [];
    const cumRaw: number[] = [];
    for (const b of activeTimelineBuckets) {
      running += b.total;
      cumRaw.push(running);
      cumValuesM.push(Math.round((running / 1_000_000) * 10) / 10);
      const pct = cost.budget > 0 ? Math.round((running / cost.budget) * 100) : 0;
      cumLabels.push(`${fmtCurrency(Math.round(running), isMasked)} (${pct}%)`);
    }
    const budgetM = Math.round((cost.budget / 1_000_000) * 10) / 10;
    const targetNetM = Math.round(((cost.budget - budget.contingencyCost) / 1_000_000) * 10) / 10;
    const maxM = Math.max(10, budgetM, ...cumValuesM);
    const ticks = niceTicks(Math.ceil(maxM), 4);

    const series: LineSeries[] = [
      {
        key: "cumulative",
        legend: "누적 예상 소진",
        color: cost.burn > 1 ? SERIES.bad : SERIES.actual,
        values: cumValuesM,
        valueLabels: cumLabels,
      },
      ...(cost.budget > 0
        ? [
            {
              key: "budgetLimit",
              legend: `승인 예산 (${fmtMillion(cost.budget, isMasked)})`,
              color: SERIES.reference,
              values: activeTimelineBuckets.map(() => budgetM),
              reference: true,
            } satisfies LineSeries,
          ]
        : []),
      ...(budget.contingencyCost > 0 && targetNetM > 0
        ? [
            {
              key: "targetLimit",
              legend: `예비비 제외 한도 (${fmtMillion(cost.budget - budget.contingencyCost, isMasked)})`,
              color: SERIES.plan,
              values: activeTimelineBuckets.map(() => targetNetM),
              dashed: true,
              reference: true,
            } satisfies LineSeries,
          ]
        : []),
    ];

    return { labels, calloutTitles, series, ticks, cumRaw };
  }, [activeTimelineBuckets, cost.budget, cost.burn, budget.contingencyCost, timeScale, isMasked]);

  const timelineVisualTable = useMemo<VisualTable>(
    () => ({
      ariaLabel: "기간별 예산 소진 추이 표",
      columns: [
        { key: "period", label: timeScale === "quarter" ? "분기" : "월" },
        { key: "mm", label: "M/M", numeric: true },
        { key: "labor", label: "직접인건비", numeric: true },
        { key: "indirect", label: "제경비·기술료", numeric: true },
        { key: "solution", label: "솔루션", numeric: true },
        { key: "total", label: "기간 합계", numeric: true },
        { key: "cum", label: "누적 소진", numeric: true },
      ],
      rows: activeTimelineBuckets.map((b, i) => ({
        key: b.ym,
        cells: [
          formatBucketFullLabel(b.ym),
          fmtMM(Math.round(b.mm * 10) / 10),
          fmtCurrency(Math.round(b.labor), isMasked),
          fmtCurrency(Math.round(b.indirect), isMasked),
          fmtCurrency(Math.round(b.solution), isMasked),
          fmtCurrency(Math.round(b.total), isMasked),
          fmtCurrency(Math.round(cumulativeModel.cumRaw[i] ?? 0), isMasked),
        ],
      })),
    }),
    [activeTimelineBuckets, cumulativeModel.cumRaw, timeScale, isMasked],
  );

  const matrixColWidth = timeScale === "quarter" ? MM_QUARTER_COL_WIDTH + 8 : MM_MONTH_COL_WIDTH + 6;
  const minMatrixTableWidth = 148 + activeTimelineBuckets.length * matrixColWidth + 120 + 68;
  const maxBucketTotal = useMemo(
    () => Math.max(0, ...activeTimelineBuckets.map((b) => b.total)),
    [activeTimelineBuckets],
  );
  const maxBucketLabor = useMemo(
    () => Math.max(0, ...activeTimelineBuckets.map((b) => b.labor)),
    [activeTimelineBuckets],
  );
  const maxBucketSolution = useMemo(
    () => Math.max(0, ...activeTimelineBuckets.map((b) => b.solution)),
    [activeTimelineBuckets],
  );

  const showIndirectRow = budget.costOptions.includeOverhead || budget.costOptions.includeTechnicalFee;
  const showExpenseRow = cost.expense > 0;
  const description = [`승인 ${fmtCurrency(cost.budget, isMasked)}`, `소진 ${burnPct}%`, `품목 ${solutions.length}개`].join(" · ");
  const solutionNarrowed = query.trim().length > 0 || categoryFilter !== "all" || statusFilter !== "all";

  return (
    <div className={styles.page}>
      <ListToolbar
        title="예산"
        description={description}
        headingAction={
          view === "solutions" && canEditCost ? (
            <FuiButton appearance="primary" icon={<Cube16Regular />} onClick={() => setSolutionDialog({ open: true, initial: null })}>
              솔루션 추가
            </FuiButton>
          ) : view === "breakdown" && canEditCost ? (
            <FuiButton appearance="primary" icon={<Money16Regular />} onClick={() => setBudgetOpen(true)}>
              예산안 변경
            </FuiButton>
          ) : undefined
        }
        sections={
          <TabList
            size="small"
            selectedValue={view}
            aria-label="예산"
            onTabSelect={(_, data) => {
              const next = data.value;
              if (next === "solutions" || next === "breakdown" || next === "timeline" || next === "actual") {
                if (next !== view) setView(next);
              }
            }}
          >
            <Tab value="solutions" icon={<Cube16Regular />}>솔루션</Tab>
            <Tab value="breakdown" icon={<DataPie16Regular />}>구조</Tab>
            <Tab value="timeline" icon={<CalendarLtr16Regular />}>기간</Tab>
            <Tab value="actual" icon={<Money16Regular />}>실제 원가</Tab>
          </TabList>
        }
        search={view === "solutions" ? <ListSearch value={query} onChange={setQuery} /> : undefined}
        filters={
          view === "solutions" ? (
            <>
              <SegmentedControl
                ariaLabel="상태"
                value={statusFilter}
                onChange={setStatusFilter}
                options={SOLUTION_STATUS_FILTER_OPTIONS}
              />
              <ListFilterMenu
                label="분류"
                value={categoryFilter}
                idleValue="all"
                options={SOLUTION_CATEGORY_FILTER_OPTIONS}
                onChange={setCategoryFilter}
              />
            </>
          ) : view === "breakdown" ? (
            <SegmentedControl
              ariaLabel="시각화 방식"
              value={breakdownVisualMode}
              onChange={setBreakdownVisualMode}
              options={[
                { value: "cards", label: "차트" },
                { value: "ink-donut", label: "잉크 도넛" },
                { value: "ink-bar", label: "잉크 바" },
              ]}
            />
          ) : view === "timeline" ? (
            <>
              <FuiFilterSelect
                label="눈금"
                ariaLabel="눈금"
                value={timeScale}
                options={[
                  { value: "month", label: "월" },
                  { value: "quarter", label: "분기" },
                ]}
                onValueChange={(value) => {
                  setTimeScale(value === "quarter" ? "quarter" : "month");
                  setHighlightedPeriodIdx(null);
                }}
              />
              {years.length > 1 ? (
                <FuiFilterSelect
                  label="기간"
                  ariaLabel="기간"
                  value={activeYear}
                  options={[{ value: "all", label: "전체" }, ...years.map((year) => ({ value: year, label: formatDate(`${year}-01`, "year") }))]}
                  onValueChange={(value) => {
                    setYearFilter(value);
                    setHighlightedPeriodIdx(null);
                  }}
                />
              ) : null}
            </>
          ) : undefined
        }
        trail={
          <>
            {view === "solutions" && (
              <Menu
                checkedValues={{ column: visibleColumns }}
                onCheckedValueChange={(_, d) => {
                  if (d.name === "column") handleColumnsChange(d.checkedItems as BudgetColKey[]);
                }}
              >
                <MenuTrigger disableButtonEnhancement>
                  <FuiButton className={styles.nowrapBtn} icon={<TableSimple16Regular />} appearance="subtle">
                    열 ({visibleDefs.length})
                    <ChevronDown12Regular />
                  </FuiButton>
                </MenuTrigger>
                <MenuPopover style={{ minWidth: 180 }}>
                  <MenuList>
                    <MenuGroup>
                      <MenuGroupHeader>
                        표시할 열 ({visibleDefs.length}/{BUDGET_COLUMNS.length})
                      </MenuGroupHeader>
                      {BUDGET_COLUMNS.map((col) => (
                        <MenuItemCheckbox key={col.key} name="column" value={col.key} disabled={col.key === "name"}>
                          {col.label}
                        </MenuItemCheckbox>
                      ))}
                    </MenuGroup>
                    <MenuDivider />
                    <MenuItem onClick={() => handleColumnsChange(DEFAULT_VISIBLE_BUDGET_COLUMNS)}>기본 열로 복원</MenuItem>
                    <MenuItem onClick={() => handleColumnsChange(BUDGET_COLUMNS.map((c) => c.key))}>모든 열 표시</MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
          </>
        }
        tools={
          <>
            {isMasked && (
              <Badge appearance="tint" color="warning" icon={<LockClosed16Regular />}>
                금액 마스킹
              </Badge>
            )}
            {view === "breakdown" && canEditCost && (
              <FuiButton className={styles.nowrapBtn} appearance="subtle" icon={<Options16Regular />} onClick={() => setOptionsOpen(true)}>
                산정 옵션
              </FuiButton>
            )}
          </>
        }
      />

      <div className={styles.body}>
        {view === "solutions" && filteredSolutions.length === 0 ? (
          <GhostEmptyState
            variant={solutionNarrowed ? "search" : "default"}
            title={solutionNarrowed ? "조건에 맞는 품목이 없습니다" : "아직 솔루션이 없습니다"}
            description={solutionNarrowed ? "상태, 분류, 검색어를 바꿔 보세요." : "품목을 넣으면 여기에 모입니다."}
          />
        ) : null}

        {view === "solutions" && filteredSolutions.length > 0 && (
          <div className={pStyles.tableWrap}>
            <table className={pStyles.table} aria-label="솔루션 및 라이선스 품목 목록">
              <thead>
                <tr>
                  {visibleDefs.map((col, i) => (
                    <th key={col.key} className={colClass(col, i === 0, true)}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredSolutions.map((sol) => {
                  const eng = resources[sol.assignedResourceId ?? ""];
                  const openSol = () => setSolutionDialog({ open: true, initial: sol });
                  const sharePct = cost.solution > 0 ? Math.round(((sol.totalCost || 0) / cost.solution) * 100) : 0;
                  const menuItems = canEditCost
                    ? [
                        { label: "상세 및 수정", onClick: openSol },
                        { label: "삭제", onClick: () => setDeleteTarget(sol) },
                      ]
                    : [];

                  return (
                    <tr key={sol.id} className={styles.rowClick} onClick={openSol}>
                      {visibleDefs.map((col, i) => {
                        const first = i === 0;
                        if (col.key === "name") {
                          return (
                            <td key={col.key} className={colClass(col, first)} onClick={(e) => e.stopPropagation()}>
                              {menuItems.length > 0 ? (
                                <RowMoreMenu label={`${sol.name} 메뉴`} items={menuItems}>
                                  <PersonRowTrigger label={`${sol.name} 메뉴`}>
                                    <TableCellLayout
                                      truncate
                                      description={sol.notes ? { className: pStyles.role, children: sol.notes } : undefined}
                                    >
                                      <Tooltip content={sol.name} relationship="description">
                                        <span className={pStyles.clip}>{sol.name}</span>
                                      </Tooltip>
                                    </TableCellLayout>
                                  </PersonRowTrigger>
                                </RowMoreMenu>
                              ) : (
                                <button
                                  type="button"
                                  className={mergeClasses(styles.nameLink, pStyles.twoLine)}
                                  onClick={openSol}
                                >
                                  <span className={pStyles.clip}>{sol.name}</span>
                                  {sol.notes && <Caption1 className={pStyles.role}>{sol.notes}</Caption1>}
                                </button>
                              )}
                            </td>
                          );
                        }
                        if (col.key === "category") {
                          return (
                            <td key={col.key} className={colClass(col, first)}>
                              <Badge appearance="tint" color={SOLUTION_CATEGORY_COLOR[sol.category]}>
                                {SOLUTION_CATEGORY_LABEL[sol.category]}
                              </Badge>
                            </td>
                          );
                        }
                        if (col.key === "vendor") {
                          return (
                            <td key={col.key} className={colClass(col, first)}>
                              {sol.vendor}
                            </td>
                          );
                        }
                        if (col.key === "billing") {
                          return (
                            <td key={col.key} className={colClass(col, first)}>
                              {BILLING_CYCLE_LABEL[sol.billingCycle]}
                            </td>
                          );
                        }
                        if (col.key === "period") {
                          return (
                            <td key={col.key} className={colClass(col, first)}>
                              {formatSolutionPeriod(sol)}
                            </td>
                          );
                        }
                        if (col.key === "unitCost") {
                          return (
                            <td key={col.key} className={colClass(col, first)}>
                              {fmtCurrency(sol.unitCost, isMasked)}
                            </td>
                          );
                        }
                        if (col.key === "quantity") {
                          return (
                            <td key={col.key} className={colClass(col, first)}>
                              {sol.quantity}
                            </td>
                          );
                        }
                        if (col.key === "totalCost") {
                          return (
                            <td key={col.key} className={colClass(col, first)}>
                              {fmtCurrency(sol.totalCost, isMasked)}
                            </td>
                          );
                        }
                        if (col.key === "share") {
                          return (
                            <td key={col.key} className={colClass(col, first)}>
                              {sharePct}%
                            </td>
                          );
                        }
                        if (col.key === "assignee") {
                          return (
                            <td key={col.key} className={colClass(col, first)}>
                              {eng ? (
                                <Tooltip content={eng.displayName} relationship="label">
                                  <span className={styles.personCell}>
                                    <PersonAvatar name={eng.displayName} size={20} />
                                  </span>
                                </Tooltip>
                              ) : (
                                <span className={styles.toneMuted}>—</span>
                              )}
                            </td>
                          );
                        }
                        return (
                          <td key={col.key} className={colClass(col, first)}>
                            {SOLUTION_STATUS_LABEL[sol.status]}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              {filteredSolutions.length > 0 && (
                <tfoot>
                  <tr className={pStyles.total}>
                    {totalIdx > 0 && (
                      <td className={mergeClasses(pStyles.td, styles.nameCol, styles.nameTotal)} colSpan={totalIdx}>
                        {filteredSolutions.length === solutions.length
                          ? `솔루션 합계 (${solutions.length}개 품목)`
                          : `필터 합계 (${filteredSolutions.length}/${solutions.length}개 품목)`}
                        {budget.targetSolutionCost > 0 && filteredSolutions.length === solutions.length && (
                          <span
                            className={mergeClasses(
                              solutionDiff < 0 ? styles.toneDanger : styles.toneMuted,
                            )}
                            style={{ marginLeft: tokens.spacingHorizontalS }}
                          >
                            · 목표 {fmtCurrency(budget.targetSolutionCost, isMasked)}{" "}
                            {solutionDiff < 0
                              ? `(${fmtCurrency(-solutionDiff, isMasked)} 초과)`
                              : `(${fmtCurrency(solutionDiff, isMasked)} 여유)`}
                          </span>
                        )}
                      </td>
                    )}
                    {totalIdx === 0 && (
                      <td className={mergeClasses(pStyles.td, pStyles.num, styles.colFixed)}>
                        솔루션 합계 {fmtCurrency(filteredSolutionSum, isMasked)}
                      </td>
                    )}
                    {totalIdx > 0 && (
                      <td className={mergeClasses(pStyles.td, pStyles.num, styles.colFixed)}>
                        {fmtCurrency(filteredSolutionSum, isMasked)}
                      </td>
                    )}
                    {totalIdx < 0 && (
                      <td className={pStyles.td} colSpan={colCount}>
                        솔루션 합계 {fmtCurrency(filteredSolutionSum, isMasked)}
                      </td>
                    )}
                    {totalIdx >= 0 && colCount - totalIdx - 1 > 0 && (
                      <td className={pStyles.td} colSpan={colCount - totalIdx - 1} />
                    )}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {view === "breakdown" && (
          <>
            {breakdownVisualMode !== "cards" ? (
              <ShareBoard
                totalLabel="계획 소진"
                countLabel="5개 비목"
                layout={breakdownVisualMode === "ink-bar" ? "bar" : "donut"}
                parts={[{ key: "cost", label: "예상 소진" }]}
                rows={segments.map((s) => ({
                  key: s.key,
                  name: s.label,
                  parts: { cost: s.value },
                }))}
                formatValue={(v) => fmtCurrency(v, isMasked)}
                note="SI 소프트웨어 사업 대가 산정 기준 비목별 구성 비중입니다. 줄을 누르면 상세 관리로 이동합니다."
                onPickRow={(key) => {
                  if (key === "labor") navigate(`/projects/${project.id}/team`);
                  else if (key === "solution") openSolutions("all");
                  else if (canEditCost) setOptionsOpen(true);
                }}
              />
            ) : (
              <div className={styles.pair}>
                <ReportVisual
                  title="비용 구조 비중 (SI 대가 산정)"
                  meta={`총 ${fmtCurrency(cost.total, isMasked)}`}
                  height={140}
                  table={breakdownVisualTable}
                  chart={() => (
                    <div className={styles.visualStack}>
                      <StackedBar
                        segments={segments}
                        ariaLabel="비용 구조 비중"
                        pickHint="항목 강조"
                        onPick={(key) => {
                          setSelectedCostKey((prev) => (prev === key ? null : key));
                        }}
                      />
                      {solutionCategorySegments.length > 0 && (
                        <div className={styles.subBlock}>
                          <span className={styles.subTitle}>
                            솔루션 분류별 구성 ({solutions.length}개 품목 · {fmtCurrency(cost.solution, isMasked)})
                          </span>
                          <StackedBar
                            segments={solutionCategorySegments}
                            ariaLabel="솔루션 분류별 구성"
                            pickHint="품목 보기"
                            onPick={(cat) => openSolutions(cat)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                />

                <ReportVisual
                  title="목표 예산 대비 예상 소진"
                  meta={`계획 대비 ${burnPct}%`}
                  height={140}
                  table={breakdownVisualTable}
                  chart={() => (
                    <div className={styles.visualStack}>
                      <PhaseProgressVisual
                        data={budgetCompareBars}
                        ariaLabel="목표 예산 대비 예상 소진 비교"
                      />
                      {laborParts.length > 0 && (
                        <div className={styles.subBlock}>
                          <span className={styles.subTitle}>
                            인건비 구성 ({fmtCurrency(totalLaborGroup, isMasked)})
                          </span>
                          <StackedBar
                            segments={laborParts}
                            ariaLabel="인건비 구성"
                            pickHint="항목 보기"
                            onPick={(key) => {
                              if (key === "labor") navigate(`/projects/${project.id}/team`);
                              else if (canEditCost) setOptionsOpen(true);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
            )}

            <div className={pStyles.tableWrap}>
              <table className={pStyles.table} aria-label="SI 대가 산정 및 예산 대사 표">
                <thead>
                  <tr>
                    <th className={mergeClasses(pStyles.th, styles.nameCol, styles.nameHead)}>계정 과목</th>
                    <th className={pStyles.th}>산정 기준 · 내역</th>
                    <th className={mergeClasses(pStyles.th, pStyles.num)}>목표 예산</th>
                    <th className={mergeClasses(pStyles.th, pStyles.num)}>예상 소진</th>
                    <th className={mergeClasses(pStyles.th, pStyles.num)}>목표 대비 (잔여/초과)</th>
                    <th className={mergeClasses(pStyles.th, pStyles.num)}>계획 대비</th>
                    <th className={mergeClasses(pStyles.th, pStyles.num)}>구성비</th>
                  </tr>
                </thead>
                <tbody>
                  {segments.slice(0, 3).map((s, idx) => {
                    const share = cost.total > 0 ? Math.round((s.value / cost.total) * 100) : 0;
                    const selected = selectedCostKey === s.key;
                    return (
                      <tr
                        key={s.key}
                        className={mergeClasses(styles.rowClick, selected && styles.rowSelected)}
                        onClick={() => {
                          if (s.key === "labor") navigate(`/projects/${project.id}/team`);
                          else if (canEditCost) setOptionsOpen(true);
                        }}
                      >
                        <td className={mergeClasses(pStyles.td, styles.nameCol)}>
                          <span className={styles.swatchRow}>
                            <span className={styles.swatchDot} style={{ backgroundColor: s.color }} aria-hidden />
                            <span>
                              1-{idx + 1}. {s.label}
                            </span>
                          </span>
                        </td>
                        <td className={mergeClasses(pStyles.td, styles.toneMuted)}>{s.hint}</td>
                        <td className={mergeClasses(pStyles.td, pStyles.num, styles.toneMuted)}>—</td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>{fmtCurrency(s.value, isMasked)}</td>
                        <td className={mergeClasses(pStyles.td, pStyles.num, styles.toneMuted)}>—</td>
                        <td className={mergeClasses(pStyles.td, pStyles.num, styles.toneMuted)}>—</td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>{share}%</td>
                      </tr>
                    );
                  })}

                  {/* 인건비 소계 (목표 인건비와 직접 대사) */}
                  {(() => {
                    const laborDiff = budget.targetLaborCost - totalLaborGroup;
                    const laborBurn = budget.targetLaborCost > 0 ? Math.round((totalLaborGroup / budget.targetLaborCost) * 100) : 0;
                    const laborShare = cost.total > 0 ? Math.round((totalLaborGroup / cost.total) * 100) : 0;
                    return (
                      <tr className={styles.subtotalRow}>
                        <td className={mergeClasses(pStyles.td, styles.nameCol, styles.subtotalRow)}>
                          1. 인건비 소계 (직접+제경비+기술료)
                        </td>
                        <td className={mergeClasses(pStyles.td, styles.toneMuted)}>
                          직접인건비 ×{" "}
                          {(
                            (budget.costOptions.includeOverhead ? budget.costOptions.overheadRatio : 1) *
                            (1 + (budget.costOptions.includeTechnicalFee ? budget.costOptions.technicalFeeRatio : 0))
                          )
                            .toFixed(3)
                            .replace(/\.?0+$/, "")}{" "}
                          배수 적용
                        </td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>
                          {fmtCurrency(budget.targetLaborCost, isMasked)}
                        </td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>
                          {fmtCurrency(totalLaborGroup, isMasked)}
                        </td>
                        <td
                          className={mergeClasses(
                            pStyles.td,
                            pStyles.num,
                            laborDiff < 0 ? styles.toneDanger : styles.toneSuccess,
                          )}
                        >
                          {laborDiff >= 0
                            ? `+${fmtCurrency(laborDiff, isMasked)} 여유`
                            : `${fmtCurrency(laborDiff, isMasked)} 초과`}
                        </td>
                        <td
                          className={mergeClasses(
                            pStyles.td,
                            pStyles.num,
                            laborDiff < 0 && styles.toneDanger,
                          )}
                        >
                          {budget.targetLaborCost > 0 ? `${laborBurn}%` : "—"}
                        </td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>{laborShare}%</td>
                      </tr>
                    );
                  })()}

                  {/* 2. 솔루션·라이선스 */}
                  {(() => {
                    const solSeg = segments[3]!;
                    const solShare = cost.total > 0 ? Math.round((solSeg.value / cost.total) * 100) : 0;
                    const selected = selectedCostKey === "solution";
                    return (
                      <tr
                        className={mergeClasses(styles.rowClick, selected && styles.rowSelected)}
                        onClick={() => openSolutions()}
                      >
                        <td className={mergeClasses(pStyles.td, styles.nameCol)}>
                          <span className={styles.swatchRow}>
                            <span className={styles.swatchDot} style={{ backgroundColor: solSeg.color }} aria-hidden />
                            <span>2. {solSeg.label}</span>
                          </span>
                        </td>
                        <td className={mergeClasses(pStyles.td, styles.toneMuted)}>
                          {solutions.length}개 품목
                        </td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>
                          {fmtCurrency(budget.targetSolutionCost, isMasked)}
                        </td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>
                          {fmtCurrency(solSeg.value, isMasked)}
                        </td>
                        <td
                          className={mergeClasses(
                            pStyles.td,
                            pStyles.num,
                            solutionDiff < 0 ? styles.toneDanger : styles.toneSuccess,
                          )}
                        >
                          {solutionDiff >= 0
                            ? `+${fmtCurrency(solutionDiff, isMasked)} 여유`
                            : `${fmtCurrency(solutionDiff, isMasked)} 초과`}
                        </td>
                        <td
                          className={mergeClasses(
                            pStyles.td,
                            pStyles.num,
                            solutionDiff < 0 && styles.toneDanger,
                          )}
                        >
                          {budget.targetSolutionCost > 0 ? `${solutionBurnPct}%` : "—"}
                        </td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>{solShare}%</td>
                      </tr>
                    );
                  })()}

                  {/* 3. 직접경비 */}
                  {(() => {
                    const expSeg = segments[4]!;
                    const expShare = cost.total > 0 ? Math.round((expSeg.value / cost.total) * 100) : 0;
                    const selected = selectedCostKey === "expense";
                    return (
                      <tr
                        className={mergeClasses(canEditCost && styles.rowClick, selected && styles.rowSelected)}
                        onClick={canEditCost ? () => setBudgetOpen(true) : undefined}
                      >
                        <td className={mergeClasses(pStyles.td, styles.nameCol)}>
                          <span className={styles.swatchRow}>
                            <span className={styles.swatchDot} style={{ backgroundColor: expSeg.color }} aria-hidden />
                            <span>3. {expSeg.label}</span>
                          </span>
                        </td>
                        <td className={mergeClasses(pStyles.td, styles.toneMuted)}>{expSeg.hint}</td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>
                          {fmtCurrency(budget.targetExpenseCost, isMasked)}
                        </td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>
                          {fmtCurrency(expSeg.value, isMasked)}
                        </td>
                        <td className={mergeClasses(pStyles.td, pStyles.num, styles.toneMuted)}>±₩0</td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>
                          {budget.targetExpenseCost > 0 ? "100%" : "—"}
                        </td>
                        <td className={mergeClasses(pStyles.td, pStyles.num)}>{expShare}%</td>
                      </tr>
                    );
                  })()}

                  {/* 4. 예비비 */}
                  <tr
                    className={canEditCost ? styles.rowClick : undefined}
                    onClick={canEditCost ? () => setBudgetOpen(true) : undefined}
                  >
                    <td className={mergeClasses(pStyles.td, styles.nameCol)}>
                      <span className={styles.swatchRow}>
                        <span className={styles.swatchDot} style={{ backgroundColor: SERIES.empty }} aria-hidden />
                        <span>4. 예비비 (Contingency)</span>
                      </span>
                    </td>
                    <td className={mergeClasses(pStyles.td, styles.toneMuted)}>
                      리스크 대응을 위한 보류 예산 (예상 소진 합계에서는 제외)
                    </td>
                    <td className={mergeClasses(pStyles.td, pStyles.num)}>
                      {fmtCurrency(budget.contingencyCost, isMasked)}
                    </td>
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.toneMuted)}>—</td>
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.toneSuccess)}>
                      +{fmtCurrency(budget.contingencyCost, isMasked)} 보류
                    </td>
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.toneMuted)}>—</td>
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.toneMuted)}>—</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className={pStyles.total}>
                    <td className={mergeClasses(pStyles.td, styles.nameCol, styles.nameTotal)}>총계 (승인 예산 대비)</td>
                    <td className={mergeClasses(pStyles.td, styles.toneMuted)}>
                      직접인건비 + 제경비 + 기술료 + 솔루션·라이선스 + 직접경비
                    </td>
                    <td className={mergeClasses(pStyles.td, pStyles.num)}>{fmtCurrency(cost.budget, isMasked)}</td>
                    <td
                      className={mergeClasses(
                        pStyles.td,
                        pStyles.num,
                        cost.burn > 1 && styles.toneDanger,
                      )}
                    >
                      {fmtCurrency(cost.total, isMasked)}
                    </td>
                    <td
                      className={mergeClasses(
                        pStyles.td,
                        pStyles.num,
                        cost.remaining < 0 ? styles.toneDanger : styles.toneSuccess,
                      )}
                    >
                      {cost.remaining >= 0
                        ? `+${fmtCurrency(cost.remaining, isMasked)} 여유`
                        : `${fmtCurrency(cost.remaining, isMasked)} 초과`}
                    </td>
                    <td
                      className={mergeClasses(
                        pStyles.td,
                        pStyles.num,
                        cost.burn > 1 && styles.toneDanger,
                      )}
                    >
                      {burnPct}%
                    </td>
                    <td className={mergeClasses(pStyles.td, pStyles.num)}>100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}

        {view === "timeline" && (
          <>
            <div className={styles.pair}>
              <ReportVisual
                title="비용 발생 추이"
                meta={
                  peakBucket
                    ? `피크 ${formatBucketLabel(peakBucket.ym)} · ${fmtMillion(peakBucket.total, isMasked)}`
                    : undefined
                }
                height={180}
                table={timelineVisualTable}
                chart={(h) => (
                  <StackedColumnsVisual
                    data={timelineStacks}
                    height={h}
                    yMax={timelinePeakM.at(-1)}
                    yTicks={timelinePeakM}
                    formatY={(v) => (isMasked ? "•••" : `${Math.round(v)}M`)}
                    ariaLabel="기간별 비용 발생 추이"
                  />
                )}
              />

              <ReportVisual
                title="누적 예산 소진 추이 (Burn-up)"
                meta={`계획 대비 ${burnPct}%`}
                height={180}
                table={timelineVisualTable}
                chart={(h) => (
                  <TrendLineVisual
                    labels={cumulativeModel.labels}
                    calloutTitles={cumulativeModel.calloutTitles}
                    series={cumulativeModel.series}
                    height={h}
                    yMin={0}
                    yMax={cumulativeModel.ticks.at(-1)}
                    yTicks={cumulativeModel.ticks}
                    formatY={(v) => (isMasked ? "•••" : `${Math.round(v)}M`)}
                    highlightIndex={highlightedPeriodIdx}
                    onPointClick={(idx) => setHighlightedPeriodIdx((prev) => (prev === idx ? null : idx))}
                    ariaLabel="누적 예산 소진 추이"
                  />
                )}
              />
            </div>

            <div className={pStyles.tableWrap}>
              <table
                className={mergeClasses(pStyles.table, pStyles.matrixTable)}
                style={{ minWidth: `${minMatrixTableWidth}px` }}
                aria-label={timeScale === "quarter" ? "분기별 예산 매트릭스" : "월별 예산 매트릭스"}
              >
                <colgroup>
                  <col style={{ width: "148px" }} />
                  {activeTimelineBuckets.map((b) => (
                    <col key={b.ym} style={{ width: `${matrixColWidth}px` }} />
                  ))}
                  <col style={{ width: "120px" }} />
                  <col style={{ width: "68px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={mergeClasses(pStyles.th, styles.rowLabel, styles.rowLabelHead)}>
                      구분 ({timeScale === "quarter" ? "분기별" : "월별"})
                    </th>
                    {activeTimelineBuckets.map((b, idx) => (
                      <th
                        key={b.ym}
                        className={mergeClasses(
                          pStyles.th,
                          pStyles.mmCol,
                          highlightedPeriodIdx === idx && styles.colHighlight,
                        )}
                        style={{ width: matrixColWidth, minWidth: matrixColWidth, maxWidth: matrixColWidth, cursor: "pointer" }}
                        onClick={() => setHighlightedPeriodIdx((prev) => (prev === idx ? null : idx))}
                        title={formatBucketFullLabel(b.ym)}
                      >
                        {formatBucketLabel(b.ym)}
                      </th>
                    ))}
                    <th className={mergeClasses(pStyles.th, pStyles.num, styles.summaryCol)}>합계</th>
                    <th className={mergeClasses(pStyles.th, pStyles.num, styles.shareCol)}>비중</th>
                  </tr>
                </thead>
                <tbody>
                  {/* M/M 행 */}
                  <tr>
                    <td className={mergeClasses(pStyles.td, styles.rowLabel)}>투입 공수 (M/M)</td>
                    {activeTimelineBuckets.map((b, idx) => (
                      <td
                        key={b.ym}
                        className={mergeClasses(
                          pStyles.td,
                          pStyles.mmCol,
                          highlightedPeriodIdx === idx && styles.colHighlight,
                        )}
                        style={{ width: matrixColWidth, minWidth: matrixColWidth, maxWidth: matrixColWidth }}
                      >
                        {b.mm > 0 ? b.mm.toFixed(1) : "·"}
                      </td>
                    ))}
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.summaryCol)}>
                      {fmtMM(cost.plannedMM)}
                    </td>
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.shareCol, styles.toneMuted)}>—</td>
                  </tr>

                  {/* 직접인건비 행 */}
                  <tr>
                    <td className={mergeClasses(pStyles.td, styles.rowLabel)}>
                      <span className={styles.swatchRow}>
                        <span className={styles.swatchDot} style={{ backgroundColor: SERIES.actual }} aria-hidden />
                        <span>직접인건비</span>
                      </span>
                    </td>
                    {activeTimelineBuckets.map((b, idx) => (
                      <td
                        key={b.ym}
                        className={mergeClasses(
                          pStyles.td,
                          pStyles.mmCol,
                          highlightedPeriodIdx === idx && styles.colHighlight,
                        )}
                        style={{
                          ...mmMonthCellStyle(b.labor, maxBucketLabor),
                          width: matrixColWidth,
                          minWidth: matrixColWidth,
                          maxWidth: matrixColWidth,
                        }}
                        title={`${formatBucketFullLabel(b.ym)} 직접인건비: ${fmtCurrency(Math.round(b.labor), isMasked)}`}
                      >
                        {fmtMillion(b.labor, isMasked)}
                      </td>
                    ))}
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.summaryCol)}>
                      {fmtCurrency(cost.directLabor, isMasked)}
                    </td>
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.shareCol)}>
                      {cost.total ? Math.round((cost.directLabor / cost.total) * 100) : 0}%
                    </td>
                  </tr>

                  {/* 제경비·기술료 행 */}
                  {showIndirectRow && (
                    <tr>
                      <td className={mergeClasses(pStyles.td, styles.rowLabel)}>
                        <span className={styles.swatchRow}>
                          <span className={styles.swatchDot} style={{ backgroundColor: SERIES.info }} aria-hidden />
                          <span>제경비 · 기술료</span>
                        </span>
                      </td>
                      {activeTimelineBuckets.map((b, idx) => (
                        <td
                          key={b.ym}
                          className={mergeClasses(
                            pStyles.td,
                            pStyles.mmCol,
                            highlightedPeriodIdx === idx && styles.colHighlight,
                          )}
                          style={{
                            ...mmMonthCellStyle(b.indirect, maxBucketLabor),
                            width: matrixColWidth,
                            minWidth: matrixColWidth,
                            maxWidth: matrixColWidth,
                          }}
                          title={`${formatBucketFullLabel(b.ym)} 제경비·기술료: ${fmtCurrency(Math.round(b.indirect), isMasked)}`}
                        >
                          {fmtMillion(b.indirect, isMasked)}
                        </td>
                      ))}
                      <td className={mergeClasses(pStyles.td, pStyles.num, styles.summaryCol)}>
                        {fmtCurrency(cost.overhead + cost.technicalFee, isMasked)}
                      </td>
                      <td className={mergeClasses(pStyles.td, pStyles.num, styles.shareCol)}>
                        {cost.total ? Math.round(((cost.overhead + cost.technicalFee) / cost.total) * 100) : 0}%
                      </td>
                    </tr>
                  )}

                  {/* 솔루션·라이선스 행 */}
                  <tr>
                    <td className={mergeClasses(pStyles.td, styles.rowLabel)}>
                      <span className={styles.swatchRow}>
                        <span className={styles.swatchDot} style={{ backgroundColor: SERIES.warn }} aria-hidden />
                        <span>솔루션 · 라이선스</span>
                      </span>
                    </td>
                    {activeTimelineBuckets.map((b, idx) => (
                      <td
                        key={b.ym}
                        className={mergeClasses(
                          pStyles.td,
                          pStyles.mmCol,
                          highlightedPeriodIdx === idx && styles.colHighlight,
                        )}
                        style={{
                          ...mmMonthCellStyle(b.solution, maxBucketSolution),
                          width: matrixColWidth,
                          minWidth: matrixColWidth,
                          maxWidth: matrixColWidth,
                        }}
                        title={`${formatBucketFullLabel(b.ym)} 솔루션: ${fmtCurrency(Math.round(b.solution), isMasked)}`}
                      >
                        {fmtMillion(b.solution, isMasked)}
                      </td>
                    ))}
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.summaryCol)}>
                      {fmtCurrency(cost.solution, isMasked)}
                    </td>
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.shareCol)}>
                      {cost.total ? Math.round((cost.solution / cost.total) * 100) : 0}%
                    </td>
                  </tr>

                  {/* 직접경비 행 */}
                  {showExpenseRow && (
                    <tr>
                      <td className={mergeClasses(pStyles.td, styles.rowLabel)}>
                        <span className={styles.swatchRow}>
                          <span className={styles.swatchDot} style={{ backgroundColor: SERIES.plan }} aria-hidden />
                          <span>직접경비</span>
                        </span>
                      </td>
                      {activeTimelineBuckets.map((b, idx) => (
                        <td
                          key={b.ym}
                          className={mergeClasses(
                            pStyles.td,
                            pStyles.mmCol,
                            highlightedPeriodIdx === idx && styles.colHighlight,
                          )}
                          style={{ width: matrixColWidth, minWidth: matrixColWidth, maxWidth: matrixColWidth }}
                          title={`${formatBucketFullLabel(b.ym)} 직접경비: ${fmtCurrency(Math.round(b.expense), isMasked)}`}
                        >
                          {fmtMillion(b.expense, isMasked)}
                        </td>
                      ))}
                      <td className={mergeClasses(pStyles.td, pStyles.num, styles.summaryCol)}>
                        {fmtCurrency(cost.expense, isMasked)}
                      </td>
                      <td className={mergeClasses(pStyles.td, pStyles.num, styles.shareCol)}>
                        {cost.total ? Math.round((cost.expense / cost.total) * 100) : 0}%
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className={pStyles.total}>
                    <td className={mergeClasses(pStyles.td, styles.rowLabel, styles.rowLabelTotal)}>기간별 합계</td>
                    {activeTimelineBuckets.map((b, idx) => (
                      <td
                        key={b.ym}
                        className={mergeClasses(
                          pStyles.td,
                          pStyles.mmCol,
                          highlightedPeriodIdx === idx && styles.colHighlight,
                        )}
                        style={{
                          ...mmMonthCellStyle(b.total, maxBucketTotal),
                          width: matrixColWidth,
                          minWidth: matrixColWidth,
                          maxWidth: matrixColWidth,
                        }}
                        title={`${formatBucketFullLabel(b.ym)} 합계: ${fmtCurrency(Math.round(b.total), isMasked)}`}
                      >
                        {fmtMillion(b.total, isMasked)}
                      </td>
                    ))}
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.summaryCol)}>
                      {fmtCurrency(cost.total, isMasked)}
                    </td>
                    <td className={mergeClasses(pStyles.td, pStyles.num, styles.shareCol)}>100%</td>
                  </tr>
                  {cost.budget > 0 && (
                    <tr className={styles.subtotalRow}>
                      <td className={mergeClasses(pStyles.td, styles.rowLabel, styles.subtotalRow)}>
                        누적 계획 대비
                      </td>
                      {activeTimelineBuckets.map((b, idx) => {
                        const cumVal = cumulativeModel.cumRaw[idx] ?? 0;
                        const pct = Math.round((cumVal / cost.budget) * 100);
                        const over = pct > 100;
                        return (
                          <td
                            key={b.ym}
                            className={mergeClasses(
                              pStyles.td,
                              pStyles.mmCol,
                              over ? styles.toneDanger : styles.toneMuted,
                              highlightedPeriodIdx === idx && styles.colHighlight,
                            )}
                            style={{ width: matrixColWidth, minWidth: matrixColWidth, maxWidth: matrixColWidth }}
                            title={`${formatBucketFullLabel(b.ym)} 누적: ${fmtCurrency(Math.round(cumVal), isMasked)} (${pct}%)`}
                          >
                            {pct}%
                          </td>
                        );
                      })}
                      <td
                        className={mergeClasses(
                          pStyles.td,
                          pStyles.num,
                          styles.summaryCol,
                          cost.burn > 1 && styles.toneDanger,
                        )}
                      >
                        승인 {fmtCurrency(cost.budget, isMasked)}
                      </td>
                      <td
                        className={mergeClasses(
                          pStyles.td,
                          pStyles.num,
                          styles.shareCol,
                          cost.burn > 1 && styles.toneDanger,
                        )}
                      >
                        {burnPct}%
                      </td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
            <Caption1 className={styles.quiet}>
              M = 백만 원 (셀을 가리키면 원 단위 상세 금액이 표시됩니다). 일시불 솔루션은 도입 월에, 구독·연간 라이선스는 계약 기간에 균등 배분됩니다.
            </Caption1>
          </>
        )}
        {view === "actual" && <ActualCostPanel />}
      </div>

      {solutionDialog.open && (
        <SolutionFormDialog
          open
          initial={solutionDialog.initial}
          resources={Object.values(resources)}
          projectStart={project.start}
          projectFinish={derived.schedule.projectFinish}
          masked={isMasked}
          onClose={() => setSolutionDialog({ open: false, initial: null })}
          onSave={(data) => {
            if (solutionDialog.initial) updateSolution(solutionDialog.initial.id, data);
            else addSolution(data);
            setSolutionDialog({ open: false, initial: null });
          }}
          onDelete={
            solutionDialog.initial
              ? () => {
                  removeSolution(solutionDialog.initial!.id);
                  setSolutionDialog({ open: false, initial: null });
                }
              : undefined
          }
        />
      )}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="솔루션 삭제"
        confirmLabel="삭제"
        width="sm"
        message="이 항목을 삭제할까요? 총 비용이 예산 소진에서 제외됩니다."
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) removeSolution(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
      {optionsOpen && (
        <CostOptionsDialog
          open
          options={budget.costOptions}
          onClose={() => setOptionsOpen(false)}
          onSave={(opts) => {
            updateCostOptions(opts);
            setOptionsOpen(false);
          }}
        />
      )}
      {budgetOpen && (
        <BudgetEditDialog
          open
          budget={budget}
          projected={{
            labor: totalLaborGroup,
            solution: cost.solution,
            total: cost.total,
          }}
          masked={isMasked}
          onClose={() => setBudgetOpen(false)}
          onSave={(patch) => {
            updateBudget(patch);
            setBudgetOpen(false);
          }}
        />
      )}
    </div>
  );
}
